import React, { useState, useEffect } from 'react';
import { Shield, Info, Users as UsersIcon } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { permissionsApi, RoleDto } from '../../services/api';

export const RolesPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<RoleDto | null>(null);

  useEffect(() => {
    setPageConfig({
      title: 'Roles y Permisos',
      description: 'Gestión de roles del sistema y sus permisos asociados',
      actions: [],
    });

    loadRoles();
  }, [setPageConfig]);

  const loadRoles = async () => {
    try {
      setIsLoading(true);
      const data = await permissionsApi.getAllRoles();
      setRoles(data);
      if (data.length > 0) {
        setSelectedRole(data[0]);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      alert('Error al cargar los roles');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Información sobre Roles</h3>
            <p className="text-sm text-blue-800">
              Los roles son predefinidos por el sistema y determinan qué acciones puede realizar cada usuario. 
              Para cambiar el rol de un usuario, edítalo desde la sección "Gestión de Usuarios".
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Roles */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Roles del Sistema</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {roles.map((role) => (
                <button
                  key={role.name}
                  onClick={() => setSelectedRole(role)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    selectedRole?.name === role.name
                      ? 'bg-green-50 border-l-4 border-green-600'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedRole?.name === role.name ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      <Shield size={20} className={
                        selectedRole?.name === role.name ? 'text-green-600' : 'text-slate-600'
                      } />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{role.name}</div>
                      <div className="text-xs text-slate-500">
                        {role.permissions.length} permisos
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Detalles del Rol Seleccionado */}
        <div className="lg:col-span-2">
          {selectedRole ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Shield size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{selectedRole.name}</h2>
                    <p className="text-sm text-slate-600">{selectedRole.description}</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <h3 className="font-semibold text-slate-900 mb-3">
                    Permisos Asignados ({selectedRole.permissions.length})
                  </h3>
                </div>

                {/* Agrupar permisos por módulo */}
                {(() => {
                  const groupedPermissions: { [module: string]: string[] } = {};
                  selectedRole.permissions.forEach(permission => {
                    const [module, action] = permission.split('.');
                    if (!groupedPermissions[module]) {
                      groupedPermissions[module] = [];
                    }
                    groupedPermissions[module].push(action);
                  });

                  return (
                    <div className="space-y-4">
                      {Object.entries(groupedPermissions).map(([module, actions]) => (
                        <div key={module} className="border border-slate-200 rounded-lg overflow-hidden">
                          <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                            <h4 className="font-medium text-slate-900">{module}</h4>
                          </div>
                          <div className="p-4">
                            <div className="flex flex-wrap gap-2">
                              {actions.map(action => (
                                <span
                                  key={action}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"
                                >
                                  {action}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
              <UsersIcon className="mx-auto mb-3 text-slate-400" size={48} />
              <p className="text-slate-500">Selecciona un rol para ver sus detalles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
